import { FormattedDateRange } from '@/components/message';
import { ColumnDef, SetAsFilter } from '@/components/searchTable';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { SingleEventSearchResult } from './eventTable';
import { SimpleTooltip } from '@/components/simpleTooltip';
import { GoSidebarExpand } from 'react-icons/go';

type Args = {
  showPreview?: ((datasetKey: string, eventID: string) => void) | false;
};

export function useEventColumns({ showPreview }: Args): ColumnDef<SingleEventSearchResult>[] {
  return useMemo(() => {
    const columns: ColumnDef<SingleEventSearchResult>[] = [
      {
        id: 'eventId',
        header: 'filters.eventId.name',
        filterKey: 'eventId', // default is same as id
        disableHiding: true,
        minWidth: 250,
        cell: ({ eventID, datasetKey }) => {
          return (
            <div className="g-inline-flex g-items-start g-w-full">
              {showPreview && typeof showPreview === 'function' && datasetKey && eventID && (
                <button
                  // Used to refocus this button after closing the preview dialog
                  data-entity-trigger={eventID}
                  className="g-pe-3 g-mt-0.5 g-ps-1 hover:g-text-primary-500 g-flex g-items-center g-pointer-events-auto"
                  onClick={() => showPreview(datasetKey, eventID)}
                >
                  <SimpleTooltip i18nKey="filterSupport.viewDetails" side="right" asChild>
                    <div className="g-flex g-items-center">
                      <GoSidebarExpand size={16} />
                    </div>
                  </SimpleTooltip>
                </button>
              )}
              <div className="g-flex-1">
                <SetAsFilter field="eventId" value={eventID}>
                  {eventID}
                </SetAsFilter>
              </div>
            </div>
          );
        },
      },
      {
        id: 'parentEventId',
        header: 'filters.parentEventId.name',
        minWidth: 150,
        cell: ({ parentEventID }) => (
          <SetAsFilter field="parentEventId" value={parentEventID}>
            {parentEventID}
          </SetAsFilter>
        ),
      },
      {
        id: 'country',
        header: 'filters.country.name',
        minWidth: 150,
        cell: ({ countryCode }) => (
          <SetAsFilter field="country" value={countryCode}>
            {countryCode && <FormattedMessage id={`enums.countryCode.${countryCode}`} />}
          </SetAsFilter>
        ),
      },
      {
        id: 'coordinates',
        header: 'filters.coordinates.name',
        cell: ({ formattedCoordinates }) => {
          if (!formattedCoordinates) return null;
          return <span className="g-text-nowrap">{formattedCoordinates}</span>;
        },
      },
      {
        id: 'year',
        header: 'filters.year.name',
        cell: ({ year }) => (
          <SetAsFilter field="year" value={year}>
            {year}
          </SetAsFilter>
        ),
      },
      {
        id: 'eventDate',
        header: 'filters.eventDate.name',
        cell: ({ eventDate }) => {
          if (!eventDate) return null;
          return (
            <span className="g-text-nowrap">
              <FormattedDateRange
                start={eventDate?.from ?? undefined}
                end={eventDate?.to ?? undefined}
                format={{ year: 'numeric', month: 'short', day: 'numeric' }}
              />
            </span>
          );
        },
      },

      {
        id: 'stateProvince',
        header: 'occurrenceFieldNames.stateProvince',
        cell: ({ stateProvince }) => {
          if (!stateProvince) return null;

          return (
            <SetAsFilter field="stateProvince" value={stateProvince}>
              {stateProvince}
            </SetAsFilter>
          );
        },
      },
    ];

    return columns;
  }, []);
}
